import { z } from 'zod'
// Profile Create form is role-driven. Keep schema permissive and include common fields used.

// Common helpers
export const ACCEPTED_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'application/pdf',
  // Office docs
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
]
const phoneSchema = z
  .string()
  .trim()
  .regex(/^\d{10}$/, { message: 'Enter a valid 10-digit number' })
  .or(z.literal('')) // allow empty string
  .transform((val) => (val === '' ? undefined : val)) // convert '' → undefined
  .optional()

// const genderNameEnum = z.enum(['Male', 'Female', 'Other'])
// Accept both title-case and lowercase inputs from the UI, normalize to title-case
const genderCanonical = z.string().transform((val) => {
  const g = String(val).trim().toLowerCase()
  if (/^m(ale)?$/.test(g)) return 'Male'
  if (/^f(emale)?$/.test(g)) return 'Female'
  return 'Other'
})
const religionEnum = z.enum(['hindu', 'muslim', 'christian', 'other'])
// Accept either raw string (e.g., 'Hindu', 'hindu') or select object {id,name}
// Normalize to lowercase enum id: 'hindu' | 'muslim' | 'christian' | 'other'
const religionNormalized = z
  .union([
    z.string(),
    z.object({ id: z.any().optional(), name: z.any().optional() }),
  ])
  .transform((val) => {
    if (typeof val === 'string') return val.trim().toLowerCase()
    const raw = (val?.id ?? val?.name ?? '').toString()
    return raw.trim().toLowerCase()
  })
  .refine((v) => v === '' || religionEnum.options.includes(v as any), {
    message:
      "Invalid enum value. Expected 'hindu' | 'muslim' | 'christian' | 'other'",
  })
const casteEnum = z.enum(['sc_st', 'obc', 'general'])
const qualificationEnum = z.enum([
  'Less than 10th',
  '10th',
  '12th',
  'Diploma',
  'UG',
  'PG',
  'PhD',
  'Post Doc',
])
const institutionTypeEnum = z.enum([
  'school',
  'polytechnic',
  'iti',
  'professional college',
  'management college',
  'arts & science college',
  'college',
  'other',
])
// Accept either string (e.g., 'School', 'Polytechnic') or select object {id,name} and normalize to lowercase
const institutionTypeNormalized = z
  .union([
    z.string(),
    z.object({ id: z.any().optional(), name: z.any().optional() }),
  ])
  .transform((val) => {
    if (typeof val === 'string') return val.trim().toLowerCase()
    const raw = (val?.id ?? val?.name ?? '').toString()
    return raw.trim().toLowerCase()
  })
  .refine((v) => v === '' || institutionTypeEnum.options.includes(v as any), {
    message:
      "Invalid enum value. Expected one of: 'school', 'polytechnic', 'iti', 'professional college', 'management college', 'arts & science college', 'college', 'other'",
  })
const heardAboutEnum = z.enum([
  'Social Media',
  'Friends',
  'Print Media',
  'Others',
])
const yesNoEnum = z.enum(['yes', 'no'])

export const formSchema = z
  .object({
    // User details
    name: z.string().trim().min(1, 'Name is required').optional(),
    mobile: phoneSchema,
    whatsapp_no: phoneSchema,

    // Role 2 fields
    dob: z
      .union([z.string(), z.date()])
      .transform((val) => {
        if (val instanceof Date && !isNaN(val.getTime())) {
          // Normalize to YYYY-MM-DD
          const yyyy = val.getFullYear()
          const mm = String(val.getMonth() + 1).padStart(2, '0')
          const dd = String(val.getDate()).padStart(2, '0')
          return `${yyyy}-${mm}-${dd}`
        }
        return (val as string) || ''
      })
      .optional(),
    gender: genderCanonical.optional(), // normalize to enum values regardless of UI case
    religion: religionNormalized.optional(), // stored as id via desc: 'id'
    religion_other: z.string().trim().optional(),
    caste: casteEnum.optional(),
    qualification: qualificationEnum.optional(),
    address: z.string().trim().min(1, 'Address is required').optional(),
    district: z.string().trim().min(1, 'District is required').optional(),
    business_idea: z
      .string()
      .trim()
      .min(1, 'Business idea is required')
      .optional(),
    investment_capability: z
      .union([z.coerce.number().nonnegative(), z.string().trim().length(0)])
      .optional()
      .transform((v) => (typeof v === 'string' ? undefined : v)),
    how_you_know_kied: heardAboutEnum.optional(),
    attended_kied_programme: yesNoEnum.optional(),
    program_details_attended: z.string().trim().optional(),

    // Role 7 fields (Expert/Mentor)
    highest_educational_qualification: z.string().trim().optional(),
    professional_expertise: z.string().trim().optional(),
    profile_url: z.string().trim().optional(),
    // Accept File | File[] | FileList; Allow pdf, docx, jpg for qualification_credentials
    qualification_credentials: z
      .any()
      .optional()
      .nullable()
      .superRefine((val, ctx) => {
        if (val == null || val === '') return
        if (val && typeof val === 'object' && 'link' in (val as any)) return
        const first: any =
          val instanceof File
            ? val
            : Array.isArray(val) && val[0] instanceof File
              ? val[0]
              : (val as any)?.target?.files?.[0] instanceof File
                ? (val as any).target.files[0]
                : typeof val === 'object' &&
                    'length' in (val as any) &&
                    (val as any)[0] instanceof File
                  ? (val as any)[0]
                  : undefined
        if (!(first instanceof File)) return
        const name = (first.name || '').toLowerCase()
        const mimeOk = ACCEPTED_FILE_TYPES.includes(first.type)
        const extOk =
          name.endsWith('.pdf') ||
          name.endsWith('.docx') ||
          name.endsWith('.jpg') ||
          name.endsWith('.jpeg')
        if (!mimeOk && !extOk) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Only .pdf, .docx, .jpg files are allowed',
            path: [],
          })
        }
        if (first.size > 10_000_000) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'File size must be less than 10MB',
            path: [],
          })
        }
      }),
    // Accept File | File[] | FileList with general allowed types
    experience_credentials: z
      .any()
      .optional()
      .nullable()
      .superRefine((val, ctx) => {
        if (val == null || val === '') return
        if (val && typeof val === 'object' && 'link' in (val as any)) return
        const first: any =
          val instanceof File
            ? val
            : Array.isArray(val) && val[0] instanceof File
              ? val[0]
              : (val as any)?.target?.files?.[0] instanceof File
                ? (val as any).target.files[0]
                : typeof val === 'object' &&
                    'length' in (val as any) &&
                    (val as any)[0] instanceof File
                  ? (val as any)[0]
                  : undefined
        if (!(first instanceof File)) return
        if (!ACCEPTED_FILE_TYPES.includes(first.type)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Unsupported file type',
            path: [],
          })
        }
        if (first.size > 10_000_000) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'File size must be less than 10MB',
            path: [],
          })
        }
      }),
    other_empanelment_details: z
      .any()
      .optional()
      .nullable()
      .superRefine((val, ctx) => {
        if (val == null || val === '') return
        if (val && typeof val === 'object' && 'link' in (val as any)) return
        const first: any =
          val instanceof File
            ? val
            : Array.isArray(val) && val[0] instanceof File
              ? val[0]
              : (val as any)?.target?.files?.[0] instanceof File
                ? (val as any).target.files[0]
                : typeof val === 'object' &&
                    'length' in (val as any) &&
                    (val as any)[0] instanceof File
                  ? (val as any)[0]
                  : undefined
        if (!(first instanceof File)) return
        if (!ACCEPTED_FILE_TYPES.includes(first.type)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Unsupported file type',
            path: [],
          })
        }
        if (first.size > 10_000_000) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'File size must be less than 10MB',
            path: [],
          })
        }
      }),

    // Role 3 fields (optional validations; will be required by UI form config)
    school_name: z
      .string()
      .trim()
      .min(1, 'School/College Name is required')
      .optional(),
    head_name: z.string().trim().min(1, 'Head name is required').optional(),
    head_designation: z
      .string()
      .trim()
      .min(1, 'Head designation is required')
      .optional(),
    head_email: z.string().trim().email('Invalid email').optional(),
    head_contact_no: phoneSchema.optional(),
    name_spc: z.string().trim().min(1, 'SPOC name is required').optional(),
    designation_spc: z
      .string()
      .trim()
      .min(1, 'SPOC designation is required')
      .optional(),
    email_spc: z.string().trim().email('Invalid email').optional(),
    contact_no_spc: phoneSchema.optional(),
    institution_type: institutionTypeNormalized.optional(),
    // Make this optional so non-role-6 (e.g., role 7) submissions don't fail validation
    institution_type_industry: z.string().trim().optional(),
    area_expertise: z
      .string()
      .trim()
      .min(1, 'Area of expertise is required')
      .optional(),
    // Make role 3 file behave like optional; validate only if a real File is provided.
    auth_letter: z
      .any()
      .optional()
      .nullable()
      .superRefine((val, ctx) => {
        // Allow empty or prefilled link objects
        if (val == null || val === '') return
        if (val && typeof val === 'object' && 'link' in (val as any)) return

        // Accept File | File[] | FileList | input change event
        const first: any =
          val instanceof File
            ? val
            : Array.isArray(val) && val[0] instanceof File
              ? val[0]
              : (val as any)?.target?.files?.[0] instanceof File
                ? (val as any).target.files[0]
                : typeof val === 'object' &&
                    'length' in (val as any) &&
                    (val as any)[0] instanceof File
                  ? (val as any)[0]
                  : undefined
        if (!(first instanceof File)) return // ignore non-file placeholders
        if (!ACCEPTED_FILE_TYPES.includes(first.type)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Unsupported file type',
            path: [],
          })
        }
        if (first.size > 5_000_000) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'File size must be less than 5MB',
            path: [],
          })
        }
      }),

    // Role 8 files (ED Club)
    // Role 5 file (Research/Institution)
    authorization_letter: z
      .any()
      .optional()
      .nullable()
      .superRefine((val, ctx) => {
        // Allow empty or prefilled link objects
        if (val == null || val === '') return
        if (val && typeof val === 'object' && 'link' in (val as any)) return

        // Accept File | File[] | FileList | input change event
        const first: any =
          val instanceof File
            ? val
            : Array.isArray(val) && val[0] instanceof File
              ? val[0]
              : (val as any)?.target?.files?.[0] instanceof File
                ? (val as any).target.files[0]
                : typeof val === 'object' &&
                    'length' in (val as any) &&
                    (val as any)[0] instanceof File
                  ? (val as any)[0]
                  : undefined
        if (!(first instanceof File)) return // ignore non-file placeholders
        if (!ACCEPTED_FILE_TYPES.includes(first.type)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Unsupported file type',
            path: [],
          })
        }
        if (first.size > 10_000_000) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'File size must be less than 10MB',
            path: [],
          })
        }
      }),

    // Role 8 files (ED Club)
    approval_letter: z
      .any()
      .optional()
      .nullable()
      .superRefine((val, ctx) => {
        if (val == null || val === '') return
        if (val && typeof val === 'object' && 'link' in (val as any)) return
        const first: any =
          val instanceof File
            ? val
            : Array.isArray(val) && val[0] instanceof File
              ? val[0]
              : (val as any)?.target?.files?.[0] instanceof File
                ? (val as any).target.files[0]
                : typeof val === 'object' &&
                    'length' in (val as any) &&
                    (val as any)[0] instanceof File
                  ? (val as any)[0]
                  : undefined
        if (!(first instanceof File)) return
        if (!ACCEPTED_FILE_TYPES.includes(first.type)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Unsupported file type',
            path: [],
          })
        }
        if (first.size > 10_000_000) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'File size must be less than 10MB',
            path: [],
          })
        }
      }),
    gst_details: z
      .any()
      .optional()
      .nullable()
      .superRefine((val, ctx) => {
        if (val == null || val === '') return
        if (val && typeof val === 'object' && 'link' in (val as any)) return
        const first: any =
          val instanceof File
            ? val
            : Array.isArray(val) && val[0] instanceof File
              ? val[0]
              : (val as any)?.target?.files?.[0] instanceof File
                ? (val as any).target.files[0]
                : typeof val === 'object' &&
                    'length' in (val as any) &&
                    (val as any)[0] instanceof File
                  ? (val as any)[0]
                  : undefined
        if (!(first instanceof File)) return
        if (!ACCEPTED_FILE_TYPES.includes(first.type)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Unsupported file type',
            path: [],
          })
        }
        if (first.size > 10_000_000) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'File size must be less than 10MB',
            path: [],
          })
        }
      }),
  })
  // Allow any additional role-specific keys without failing validation
  .passthrough()
  .superRefine((data, ctx) => {
    // Conditional: religion_other required when religion === 'other'
    if (
      data.religion === 'other' &&
      (!data.religion_other || data.religion_other.trim() === '')
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['religion_other'],
        message: 'Please specify your religion',
      })
    }

    // Conditional: program_details_attended required when attended_kied_programme === 'yes'
    if (
      data.attended_kied_programme === 'yes' &&
      (!data.program_details_attended ||
        data.program_details_attended.trim() === '')
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['program_details_attended'],
        message: 'Please specify the program name',
      })
    }
  })

export type AdminSchema = z.infer<typeof formSchema>
